import React, { useMemo } from "react";
import type { ReactElement } from "react";

import DbsStatus from "pianofunclub-crm/components/Accounts/DbsStatus";
import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";

import type { CellProps } from "pianofunclub-shared/components/Base/Cell";
import Row from "pianofunclub-shared/components/Base/Row";
import type { TableData } from "pianofunclub-shared/components/Base/Row";
import { WarningIcon } from "pianofunclub-shared/components/Other/Icons";
import type { LoadAccounts_query_profiles$data } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccounts_query_profiles.graphql";
import type { PianofunclubProfileModelAccountTypeChoices } from "pianofunclub-shared/relay/graphql/general/__generated__/MeQuery.graphql";
import {
  discountTextConverter,
  schoolYearConverter,
  titleCaseConverter,
} from "pianofunclub-shared/utils/converters";
import { getFullName } from "pianofunclub-shared/utils/extractors";

interface Props {
  cellProps?: CellProps;
  currentPageIndex: number;
  data: NonNullable<
    NonNullable<NonNullable<LoadAccounts_query_profiles$data["profiles"]>["edges"][0]>["node"]
  >;
  flexArray: number[];
  onPressProfile: (
    id?: string | null,
    accountType?: PianofunclubProfileModelAccountTypeChoices | null
  ) => void;
  onPressSchool: (schoolId?: string | null) => void;
  rowHeight?: number;
  tableBorderColor?: string;
  tableBorderWidth?: number;
}

const AccountTableRow = (props: Props): ReactElement => {
  const {
    cellProps,
    currentPageIndex,
    data,
    flexArray,
    onPressProfile,
    onPressSchool,
    rowHeight,
    tableBorderColor,
    tableBorderWidth,
  } = props;

  const rowData = useMemo(() => {
    const row: TableData = [
      {
        data:
          currentPageIndex !== 1
            ? (data.accountNumber ?? "")
            : (data.primaryParent?.accountNumber ?? ""),
        onPress: () => {
          if (currentPageIndex !== 1) {
            onPressProfile(data.id, data.accountType);
          } else {
            onPressProfile(data.primaryParent?.id, "PARENT");
          }
        },
      },
      {
        data: getFullName(data.user.firstName, data.user.lastName),
        onPress: () => onPressProfile(data.id, data.accountType),
      },
    ];
    if (currentPageIndex !== 1) {
      row.push(
        {
          data: data.user.email ?? "",
          onPress: () => onPressProfile(data.id, data.accountType),
        },
        {
          data: data.phoneNumber ?? "",
          icon: data.hasInvalidPhoneNumber ? <WarningIcon color="yello.500" size="5" /> : undefined,
          onPress: () => onPressProfile(data.id, data.accountType),
        }
      );
    }
    switch (currentPageIndex) {
      case 0:
        return row.concat([
          {
            data: data.status ? (
              <ItemStatus
                isTransparent
                status={data.status}
                textProps={{
                  fontFamily: "Poppins-Light",
                }}
              />
            ) : (
              ""
            ),
            onPress: data.status ? () => onPressProfile(data.id, data.accountType) : undefined,
          },
        ]);
      case 1:
        return row.concat([
          {
            data: getFullName(
              data.primaryParent?.user.firstName,
              data.primaryParent?.user.lastName
            ),
            onPress: () => onPressProfile(data.primaryParent?.id, "PARENT"),
          },
          {
            data: titleCaseConverter(data.school?.name) ?? "",
            onPress: () => onPressSchool(data.school?.id),
          },
          {
            data: schoolYearConverter(data.schoolYear),
          },
          {
            data: discountTextConverter(data.discountCategory, data.discountPercentage),
          },
          {
            data: data.status ? (
              <ItemStatus
                isTransparent
                status={data.status}
                textProps={{
                  fontFamily: "Poppins-Light",
                }}
              />
            ) : (
              ""
            ),
            onPress: data.status ? () => onPressProfile(data.id, data.accountType) : undefined,
          },
        ]);
      case 2:
        return row.concat([
          {
            data: (
              <DbsStatus
                dbsExpirationDate={data.dbsExpirationDate}
                dbsNumber={data.dbsNumber}
                isTransparent
                textProps={{
                  fontFamily: "Poppins-Light",
                }}
              />
            ),
          },
          {
            data: data.status ? (
              <ItemStatus
                isTransparent
                status={data.status}
                textProps={{
                  fontFamily: "Poppins-Light",
                }}
              />
            ) : (
              ""
            ),
          },
        ]);
      default:
        return row.concat([
          {
            data: data.user.isSuperuser ? "Maximum" : "Normal",
          },
        ]);
    }
  }, [
    currentPageIndex,
    data.accountNumber,
    data.primaryParent?.accountNumber,
    data.primaryParent?.id,
    data.primaryParent?.user.firstName,
    data.primaryParent?.user.lastName,
    data.user.firstName,
    data.user.lastName,
    data.user.email,
    data.user.isSuperuser,
    data.id,
    data.accountType,
    data.phoneNumber,
    data.hasInvalidPhoneNumber,
    data.status,
    data.school?.name,
    data.school?.id,
    data.schoolYear,
    data.discountCategory,
    data.discountPercentage,
    data.dbsExpirationDate,
    data.dbsNumber,
    onPressProfile,
    onPressSchool,
  ]);

  return (
    <Row
      cellProps={cellProps}
      data={rowData}
      flexArray={flexArray}
      rowHeight={rowHeight}
      rowIndex={0}
      tableBorderColor={tableBorderColor}
      tableBorderWidth={tableBorderWidth}
    />
  );
};

export default React.memo(AccountTableRow);
